import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calendar, Pencil, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { useDecideApproval } from '../../../hooks/mutations';
import DatePicker from '../../task/pickers/DatePicker';
import { useDateTime } from '../../../contexts/DateTimeContext';
import { dayName, formatDay, recurrenceText } from '../cadence';
import { CardRow, CardShell, ResolvedCard, ghostBtn, inputCls, outcomeOf, parseResult, primaryBtn, resultError, secondaryBtn } from './CardShell';

const TYPES = ['daily', 'weekdays', 'weekly', 'biweekly', 'monthly', 'custom'];
const field = 'h-8 rounded-lg border border-input bg-background px-2 text-[13px] text-foreground focus:outline-none focus:border-amber-500/60 dark:[color-scheme:dark]';

const RepeatEditor = ({ value, onChange, lang, t }) => (
  <div className="space-y-2">
    <div className="flex flex-wrap gap-1">
      {TYPES.map((type) => (
        <button key={type} type="button" onClick={() => onChange({ type, interval: 1, ...(type === 'custom' ? { days_of_week: value.days_of_week?.length ? value.days_of_week : [0] } : {}) })}
          className={`h-7 px-2.5 rounded-full border text-xs ${value.type === type
            ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium'
            : 'border-input text-muted-foreground hover:text-foreground'}`}>
          {t(`bots.repeat.type_${type}`)}
        </button>
      ))}
    </div>
    {['daily', 'weekly', 'monthly'].includes(value.type) && (
      <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
        {t('bots.repeat.interval')}
        <input type="number" min={1} max={365} value={value.interval || 1} className={`${field} w-20`}
          onChange={(e) => onChange({ ...value, interval: Math.max(1, Number(e.target.value) || 1) })} />
      </label>
    )}
    {value.type === 'custom' && (
      <div className="flex flex-wrap gap-1">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => {
          const days = value.days_of_week || [];
          const on = days.includes(d);
          return (
            <button key={d} type="button" aria-pressed={on}
              onClick={() => { const next = on ? days.filter((x) => x !== d) : [...days, d].sort(); if (next.length) onChange({ ...value, days_of_week: next }); }}
              className={`h-7 min-w-[38px] px-1.5 rounded-md border text-xs capitalize ${on
                ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-input text-muted-foreground'}`}>
              {dayName(d, lang, 'short')}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

/** The bot wants a task that repeats (and maybe to do it itself each time). */
const RecurringTaskCard = ({ approval, botName, compact, onOpenTask }) => {
  const { t, i18n } = useTranslation();
  const { toLocalDateString } = useDateTime();
  const lang = i18n.language;
  const decide = useDecideApproval();
  const card = approval.card;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: card.title, recurrence: card.recurrence, due_date: card.due_date || '',
    assign_to_me: card.assign_to_me });

  if (approval.status !== 'pending') {
    const final = { ...card, ...(approval.edits || {}) };
    const error = resultError(approval);
    const task = parseResult(approval);
    const done = approval.status === 'approved' && !error;
    return (
      <ResolvedCard icon={Repeat} testid="recurring-card-resolved" ok={done}
        text={`${final.title} · ${recurrenceText(t, final.recurrence, lang)}`}
        outcome={error ? t('bots.cards.failed') : outcomeOf(approval, t, t('bots.cards.task_done'))}
        action={done && task?.id && onOpenTask ? (
          <button type="button" onClick={() => onOpenTask(task.id)} title={t('bots.cards.open_task')}
            className="flex-shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground">
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : null} />
    );
  }

  const busy = decide.isPending;
  const send = (approve) => {
    let edits;
    if (approve && editing) {
      edits = {};
      if (draft.title !== card.title) edits.title = draft.title;
      if (JSON.stringify(draft.recurrence) !== JSON.stringify(card.recurrence)) edits.recurrence = draft.recurrence;
      if ((draft.due_date || '') !== (card.due_date || '') && draft.due_date) edits.due_date = draft.due_date;
      if (draft.assign_to_me !== card.assign_to_me) edits.assign_to_me = draft.assign_to_me;
      if (!Object.keys(edits).length) edits = undefined;
    }
    decide.mutate({ approvalId: approval.id, approve, edits }, {
      onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
    });
  };

  return (
    <CardShell icon={Repeat} testid="recurring-card" waiting compact={compact}
      eyebrow={t(`bots.cards.recurring_eyebrow_${card.action}`, { bot: botName })}
      title={editing ? null : card.title}
      footer={(
        <>
          {!editing && (
            <button type="button" className={secondaryBtn} onClick={() => setEditing(true)} disabled={busy}>
              <Pencil className="w-3.5 h-3.5" />{t('bots.cards.edit')}
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={() => send(false)} disabled={busy} data-testid="card-decline">
            {t('bots.cards.not_now')}
          </button>
          <button type="button" className={primaryBtn} onClick={() => send(true)} disabled={busy || (editing && !draft.title.trim())}
            data-testid="card-confirm">
            {t(`bots.cards.recurring_confirm_${card.action}`)}
          </button>
        </>
      )}>
      {editing ? (
        <div className="space-y-2.5 pb-1">
          <input value={draft.title} maxLength={200} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className={inputCls} aria-label={t('bots.cards.task_title')} />
          <RepeatEditor value={draft.recurrence} onChange={(recurrence) => setDraft((d) => ({ ...d, recurrence }))} lang={lang} t={t} />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            {t('bots.cards.starts')}
            {/* The same date picker as a task's detail (the first occurrence's day) */}
            <DatePicker value={draft.due_date || null}
              onChange={(date) => setDraft((d) => ({ ...d, due_date: date ? toLocalDateString(date) : '' }))}>
              <button type="button" className={`${field} inline-flex items-center gap-1.5`} data-testid="card-starts">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                {draft.due_date ? formatDay(draft.due_date, lang) : t('tasks.no_date')}
              </button>
            </DatePicker>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <input type="checkbox" checked={!!draft.assign_to_me} onChange={(e) => setDraft((d) => ({ ...d, assign_to_me: e.target.checked }))}
              className="accent-amber-500" />
            {t('bots.cards.bot_does_it', { bot: botName })}
          </label>
        </div>
      ) : (
        <div>
          <CardRow label={t('bots.cards.repeats')}>{recurrenceText(t, card.recurrence, lang)}</CardRow>
          {card.due_date && <CardRow label={t('bots.cards.starts')}>{formatDay(card.due_date, lang)}</CardRow>}
          {(card.next || []).length > 0 && (
            <CardRow label={t('bots.cards.then')}>
              <span className="flex flex-wrap gap-1">
                {card.next.map((n) => <span key={n} className="px-1.5 py-0.5 rounded-md bg-accent text-xs">{formatDay(n, lang)}</span>)}
              </span>
            </CardRow>
          )}
          {card.assign_to_me && (
            <CardRow label={t('bots.cards.who')}>{t('bots.cards.bot_does_it', { bot: botName })}</CardRow>
          )}
          {card.description && <p className="mt-1.5 text-[13px] text-muted-foreground line-clamp-3">{card.description}</p>}
        </div>
      )}
    </CardShell>
  );
};

export default RecurringTaskCard;
